import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { PatientActivity, PatientActivityType } from '@uplift/api';
import { providerPatientRoute } from '@uplift/constants';
import { useL10n } from '@uplift/hooks';
import {
  useAssessmentSubmissionsQuery, usePatientActivityQuery, useUserDisplayNameQuery
} from '@uplift/queries';

import {
  NavigationBackButton, ProviderDialogNavigation, ReviewLayout, ReviewLayoutContentControlsProvider
} from '../../../Layout';
import { Loader } from '../../../Loader';
import { Modal } from '../../../Modal';
import {
  AssessmentsProgressChartWithDetails
} from '../../../pages/ProviderPatientProfile/AssessmentsProgressCharts';
import {
  ActivityDetailedView
} from './components/ActivityDetailedView/ActivityDetailedView.component';
import { SidebarActivityList } from './components/SidebarActivityList';

const viewableActivityTypes: Record<PatientActivityType, boolean> = {
  [PatientActivityType.newMatchByAdmin]: false,
  [PatientActivityType.newMatchByReferral]: false,
  [PatientActivityType.newMatchByRematch]: false,
  [PatientActivityType.newMatchByPsychiatryBooking]: false,
  [PatientActivityType.rematched]: false,

  [PatientActivityType.assessmentFormInitial]: true,
  [PatientActivityType.assessmentFormExpired]: true,
  [PatientActivityType.assessmentFormCompleted]: true,

  [PatientActivityType.formInitial]: true,
  [PatientActivityType.formExpired]: true,
  [PatientActivityType.formCompleted]: true,

  [PatientActivityType.therapySessionInitial]: false,
  [PatientActivityType.therapySessionCompleted]: true,
  [PatientActivityType.therapySessionSubmitted]: true,
  [PatientActivityType.therapySessionCancelled]: true,

  [PatientActivityType.psychiatrySessionInitial]: false,
  [PatientActivityType.psychiatrySessionCompletedOnCanvas]: false,
  [PatientActivityType.psychiatrySessionCompleted]: true,
  [PatientActivityType.psychiatrySessionSubmitted]: true,
  [PatientActivityType.psychiatrySessionCancelled]: true,

  [PatientActivityType.document]: true,

  [PatientActivityType.noteInitial]: false,
  [PatientActivityType.noteSubmitted]: true,

  [PatientActivityType.referral]: true,

  [PatientActivityType.conversation]: true,
};


const ActivityPreviewControlsProvider: React.FC<{
  patientId: string;
  selectedItem?: PatientActivity;
  onSelectItem: (item: PatientActivity | null) => void;
}> = ({ patientId, selectedItem, onSelectItem, ...props }) => {
  const { data } = usePatientActivityQuery(patientId, {});
  const viewableFlatData = React.useMemo(() => {
    return data?.pages.flatMap(p => p.data).filter(d => viewableActivityTypes[d.activityType]) ?? [];
  }, [ data ]);

  return (
    <ReviewLayoutContentControlsProvider
      items={viewableFlatData}
      findSelectedIndex={(item) => selectedItem?.datetime === item.datetime}
      selectedItem={selectedItem}
      onSelectItem={onSelectItem}
      {...props}
    />
  );
};


const useStyles = makeStyles()(theme => ({
  additionalContent: {
    width: '100%',
    maxWidth: '384px',
  },
  progressChart: {
    borderRight: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(0, 2),
  },
}));

export type PatientChartReviewProps = {
  patientId: string;
  selectedItem?: PatientActivity;
  onSelectItem: (item: PatientActivity | null) => void;
};

export type PatientChartReviewModalProps = PatientChartReviewProps;

export const PatientChartReviewModal: React.FC<PatientChartReviewModalProps> = (props) => {
  const history = useHistory();
  const { data: patientName } = useUserDisplayNameQuery(props.patientId, { enabled: !!props?.patientId });

  const formKey = useMemo(() => {
    if (props.selectedItem?.activityType === PatientActivityType.assessmentFormCompleted) {
      return props.selectedItem.formKey;
    }

    return undefined;
  }, [ props.selectedItem ]);

  const { data: submissions } = useAssessmentSubmissionsQuery(props.patientId, formKey, { suspense: true });

  const handleBack = useCallback(() => {
    props.onSelectItem(null);
    history.push(providerPatientRoute.createLink({ patientId: props.patientId }));
  }, [ history, props ]);

  const { classes } = useStyles();
  const l10n = useL10n();
  const isAdditionalContentNull = !submissions || (submissions.length > 1);

  const additionalContent = useMemo(() => {
    if (props.selectedItem?.activityType !== PatientActivityType.assessmentFormCompleted || !isAdditionalContentNull) {
      return null;
    }

    return (
      <Box className={classes.additionalContent}>
        <Box className={classes.progressChart}>
          <AssessmentsProgressChartWithDetails
            patientId={props.selectedItem.submission.userId}
            assessmentId={props.selectedItem.form.formId}
            assessmentKey={props.selectedItem.formKey}
          />
        </Box>
      </Box>
    );
  }, [ classes.additionalContent, classes.progressChart, isAdditionalContentNull, props.selectedItem ]);

  return (
    <Modal
      open={!!props.selectedItem}
      onClose={handleBack}
      fullScreen
    >
      <React.Suspense fallback={<Loader />}>
        <ProviderDialogNavigation
          position="sticky"
          title="Chart Review"
          leftActions={(
            <NavigationBackButton onClick={handleBack}>
              {l10n.clientProfile.assessments.navButtonBack(patientName?.first ?? '')}
            </NavigationBackButton>
          )}
        />
        <ActivityPreviewControlsProvider
          patientId={props.patientId}
          selectedItem={props.selectedItem}
          onSelectItem={props.onSelectItem}
        >
          <ReviewLayout
            sidebar={(
              <SidebarActivityList
                patientId={props.patientId}
                selectedItem={props.selectedItem}
                onSelectItem={props.onSelectItem}
              />
            )}
            content={props.selectedItem && (
              <ActivityDetailedView activityItem={props.selectedItem} />
            )}
            additionalContent={additionalContent}
          />
        </ActivityPreviewControlsProvider>
      </React.Suspense>
    </Modal>
  );
};
